import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ProviderKind } from '../../../__generated__/graphql';
import { Button } from '../../components/button';
import {
  Alert,
  Badge,
  Card,
  CardBody,
  CardHeader,
  Field,
  FormGrid,
  Input,
  InlineRow,
  LoadingBlock,
  Muted,
  Select,
  Stack,
} from '../../components/ui';
import { MY_SERVICE_PROVIDER } from '../../services/serviceService/queries';
import {
  useMyServiceProviderQuery,
  useUpsertServiceProviderMutation,
} from '../../services/serviceService/__generated__/queries.generated';
import { serviceErrorMessage } from './serviceErrors';
import { PROVIDER_TAX_INFO_PATH } from './servicePaths';

export const PROVIDER_KIND_LABELS: Record<ProviderKind, string> = {
  [ProviderKind.Individual]: 'Just me',
  [ProviderKind.Company]: 'A company with workers',
};

/**
 * The Provider profile (spec 019 D14, D18): a company or a person, and the
 * name customers see. A person is their own only worker, so their login comes
 * with the worker; a company invites one per worker.
 */
export function ProviderProfileCard() {
  const query = useMyServiceProviderQuery({ fetchPolicy: 'cache-and-network' });
  const provider = query.data?.myServiceProvider ?? null;
  const [upsert, { loading: saving }] = useUpsertServiceProviderMutation({
    refetchQueries: [{ query: MY_SERVICE_PROVIDER }],
  });
  const [editing, setEditing] = useState(false);
  const [kind, setKind] = useState<ProviderKind>(ProviderKind.Individual);
  const [businessName, setBusinessName] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (provider) {
      setKind(provider.providerKind);
      setBusinessName(provider.businessName);
    }
  }, [provider]);

  if (query.loading && !query.data) return <LoadingBlock label="Loading your Provider profile" />;

  const showForm = editing || !provider;

  const onSave = async () => {
    const name = businessName.trim();
    if (!name || name.length > 120) {
      setError('Enter a name of 1 to 120 characters');
      return;
    }
    setError(null);
    try {
      await upsert({ variables: { input: { providerKind: kind, businessName: name } } });
      toast.success('Provider profile saved');
      setEditing(false);
    } catch (e) {
      setError(serviceErrorMessage(e, 'Could not save the profile'));
    }
  };

  return (
    <Card data-testid="provider-profile">
      <CardHeader
        title={provider ? provider.businessName : 'Set up your Provider profile'}
        description={
          provider
            ? PROVIDER_KIND_LABELS[provider.providerKind]
            : 'Tell customers who you are. You can change this later.'
        }
        actions={
          provider && !editing ? (
            <Button
              type="button"
              theme="secondary"
              size="sm"
              text="Edit"
              onClick={() => setEditing(true)}
            />
          ) : undefined
        }
      />
      <CardBody>
        {showForm ? (
          <Stack $gap={4}>
            {error && (
              <Alert tone="error" title="Not saved">
                {error}
              </Alert>
            )}
            <FormGrid>
              <Field label="Who does the jobs?" htmlFor="provider-kind">
                <Select
                  id="provider-kind"
                  value={kind}
                  onChange={event => setKind(event.target.value as ProviderKind)}
                  data-testid="provider-kind"
                >
                  {Object.values(ProviderKind).map(value => (
                    <option key={value} value={value}>
                      {PROVIDER_KIND_LABELS[value]}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field
                label={
                  kind === ProviderKind.Company ? 'Business name' : 'Your name or business name'
                }
                htmlFor="provider-name"
                required
              >
                <Input
                  id="provider-name"
                  value={businessName}
                  maxLength={120}
                  onChange={event => setBusinessName(event.target.value)}
                  data-testid="provider-name"
                />
              </Field>
            </FormGrid>
            <InlineRow>
              <Button
                type="button"
                text="Save"
                onClick={onSave}
                isLoading={saving}
                dataTestId="provider-save"
              />
              {provider && (
                <Button
                  type="button"
                  theme="text"
                  text="Cancel"
                  onClick={() => setEditing(false)}
                />
              )}
            </InlineRow>
          </Stack>
        ) : (
          <Stack $gap={4}>
            <InlineRow>
              <Badge tone={provider!.insuranceVerified ? 'success' : 'neutral'}>
                {provider!.insuranceVerified ? 'Insured' : 'No insurance on file'}
              </Badge>
              <Badge
                tone={provider!.taxInfoComplete ? 'success' : 'warning'}
                data-testid="provider-tax-badge"
              >
                {provider!.taxInfoComplete ? 'Tax info on file' : 'Tax info needed'}
              </Badge>
            </InlineRow>
            {!provider!.taxInfoComplete && (
              <Muted $size="sm">
                Add your tax information (W-9) before you list a worker.{' '}
                <Link to={PROVIDER_TAX_INFO_PATH}>Add tax info</Link>
              </Muted>
            )}
          </Stack>
        )}
      </CardBody>
    </Card>
  );
}
